import {
    addHours, addMilliseconds, format, intervalToDuration, formatDuration, parse,
} from 'date-fns';
import { formatInTimeZone, toDate, zonedTimeToUtc } from 'date-fns-tz';

import {
    DateFormatName,
    DateFormat as DateFormatSetting,
    DateFormat as GlobalDateFormat,
    useGlobalSettingsStore,
} from '@/vue/stores/globalSettings';
import { TimeFormat } from '../types/date';
import { neverReached } from '../typeAssertions/never';
import { useTimezoneStore } from '@/vue/stores/timezone';

export enum DateFormat {
    UK = 'dd/MM/yyyy',
    US = 'MM/dd/yyyy',
}

export enum DateFormFormat {
    UK = 'dd/MM/yyyy',
    US = 'MM/dd/yyyy',
}

const COMPARE_DATE_FORMAT = DateFormFormat.UK;

export default class DateHelper {
    public static toDateFormat(date: Date, withTimezone = true): string {
        return DateHelper.toFormat(date, DateHelper.dateFormat, withTimezone);
    }

    public static toDateFormFormat(date: Date, withTimezone = true): string {
        return DateHelper.toFormat(date, DateHelper.dateFormFormat, withTimezone);
    }

    public static toDateTimeFormat(date: Date, withTimezone = true): string {
        return DateHelper.toFormat(date, DateHelper.dateTimeFormat, withTimezone);
    }

    public static toTimeFormat(date: Date, withTimezone = true): string {
        return DateHelper.toFormat(date, DateHelper.timeFormat, withTimezone);
    }

    public static toFormat(date: Date, dateFormat: string, withTimezone = true): string {
        if (!withTimezone) {
            return format(date, dateFormat);
        }

        const timezoneStore = useTimezoneStore();
        return formatInTimeZone(date, timezoneStore.serverTimezone, dateFormat);
    }

    public static toTitleFormat(): string {
        const { dateFormat } = useGlobalSettingsStore();

        if (!dateFormat) {
            return 'DD/MM/YYYY';
        }

        const titleFormats = {
            [GlobalDateFormat.UK]: 'DD/MM/YYYY',
            [GlobalDateFormat.US]: 'MM/DD/YYYY',
        };

        return titleFormats[dateFormat];
    }

    public static setServerTimezone(date: Date): Date {
        const timezoneStore = useTimezoneStore();

        const dateFormatted = format(date, 'yyyy-MM-dd HH:mm:ss');

        return toDate(dateFormatted, { timeZone: timezoneStore.serverTimezone });
    }

    /** Mirrors PHP DateTimeImmutable::add('P{n}D') on the server timezone wall clock. */
    public static addDaysInServerTimezone(date: Date, days: number): Date {
        const { serverTimezone } = useTimezoneStore();
        const wallClockFormat = 'yyyy-MM-dd HH:mm:ss';
        const wallClock = formatInTimeZone(date, serverTimezone, wallClockFormat);
        const [year, month, day, hours, minutes, seconds] = wallClock
            .split(/[- :]/)
            .map(Number);
        const target = new Date(Date.UTC(year, month - 1, day + days, hours, minutes, seconds))
            .toISOString()
            .slice(0, 19)
            .replace('T', ' ');
        const offsetAt = (instant: Date): number => DateHelper.offsetAt(instant, serverTimezone);
        const sameOffsetResult = new Date(Date.parse(`${target.replace(' ', 'T')}Z`) - offsetAt(date));
        if (formatInTimeZone(sameOffsetResult, serverTimezone, wallClockFormat) === target) {
            return sameOffsetResult;
        }

        const result = zonedTimeToUtc(target, serverTimezone);

        if (formatInTimeZone(result, serverTimezone, wallClockFormat) === target) {
            return result;
        }

        const gap = offsetAt(addHours(result, 24)) - offsetAt(addHours(result, -24));

        return addMilliseconds(result, gap);
    }

    /**
     * Mirrors PHP DateTime on the server timezone: a repeated wall clock time
     * takes its first occurrence, a skipped one moves forward by the gap.
     */
    public static fromServerWallClock(
        year: number,
        monthIndex: number,
        day: number,
        hours: number,
        minutes: number,
    ): Date {
        const { serverTimezone } = useTimezoneStore();
        const wallClock = Date.UTC(year, monthIndex, day, hours, minutes);
        const dayMs = 24 * 3600 * 1000;
        const offsetAt = (instant: number): number => DateHelper.offsetAt(instant, serverTimezone);
        const offsetBefore = offsetAt(wallClock - dayMs);
        const matches = [wallClock - offsetBefore, wallClock - offsetAt(wallClock + dayMs)]
            .filter((instant) => instant + offsetAt(instant) === wallClock);

        return new Date(matches.length > 0 ? Math.min(...matches) : wallClock - offsetBefore);
    }

    public static getServerTimezoneOffset(): number {
        const timezoneStore = useTimezoneStore();

        return timezoneStore.timezoneOffset;
    }

    public static equalsDate(first: Date, second: Date, withTimezone = true): boolean {
        const firstFormatted = this.toFormat(first, COMPARE_DATE_FORMAT, withTimezone);
        const secondFormatted = this.toFormat(second, COMPARE_DATE_FORMAT, withTimezone);

        return firstFormatted === secondFormatted;
    }

    public static toSecondsOffset(date: Date): number {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        return (hours * 60 + minutes) * 60 + seconds;
    }

    /**
     * This method does not support a duration of more than 24 hours (hours will be reset).
     */
    public static toDurationTimeFormat(duration: number): string {
        const durationObject = intervalToDuration({ start: 0, end: duration * 1000 });

        return formatDuration(durationObject, {
            format: ['hours', 'minutes', 'seconds'],
            zero: true,
            delimiter: ':',
            locale: {
                formatDistance: (_token, count) => String(count).padStart(2, '0'),
            },
        });
    }

    /**
     *
     * @param date string in  DateHelper.dateFormFormat
     * @returns
     */
    public static toDate(date: string): Date {
        const timezoneStore = useTimezoneStore();

        const dateObject = parse(date, DateHelper.dateFormFormat, new Date());
        return toDate(dateObject, { timeZone: timezoneStore.serverTimezone });
    }

    private static offsetAt(instant: Date | number, timeZone: string): number {
        return Date.parse(formatInTimeZone(instant, timeZone, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"))
            - new Date(instant).getTime();
    }

    protected static get dateFormat(): DateFormat {
        const { dateFormat } = useGlobalSettingsStore();

        if (!dateFormat) {
            return DateFormat.UK;
        }

        const dateFormats = {
            [DateFormatSetting.UK]: DateFormat.UK,
            [DateFormatSetting.US]: DateFormat.US,
        };

        return dateFormats[dateFormat];
    }

    protected static get dateFormFormat(): DateFormFormat {
        const { dateFormat } = useGlobalSettingsStore();

        if (!dateFormat) {
            return DateFormFormat.UK;
        }

        const dateFormats = {
            [DateFormatSetting.UK]: DateFormFormat.UK,
            [DateFormatSetting.US]: DateFormFormat.US,
        };

        return dateFormats[dateFormat];
    }

    protected static get timeFormat(): string {
        const { timeFormat } = useGlobalSettingsStore();

        if (timeFormat === TimeFormat.TWELVE_HOUR) {
            return 'h:mm a';
        }

        if (timeFormat === TimeFormat.TWENTY_FOUR_HOUR) {
            return 'HH:mm';
        }

        neverReached(timeFormat);

        return '';
    }

    protected static get dateTimeFormat(): string {
        const { dateFormatName } = useGlobalSettingsStore();
        const { timeFormat } = DateHelper;

        const dateFormats = {
            [DateFormatName.UK]: `${timeFormat} · dd/MM/yyyy`,
            [DateFormatName.US]: `${timeFormat} · MM/dd/yyyy`,
        };

        return dateFormats[dateFormatName];
    }
}
